import { Checkbox } from "@arcadeai/design-system/components/ui/atoms/checkbox";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { type ReactNode, useId } from "react";

/**
 * Shared bordered checkbox control used in the MCP server picker header row.
 * The full pill is the checkbox target so the compact control is easy to hit.
 */
export function HeaderCheckboxToggle({
  checked,
  className,
  indeterminate = false,
  label,
  onCheckedChange,
  tooltip,
}: {
  checked: boolean;
  className?: string;
  indeterminate?: boolean;
  label: string;
  onCheckedChange: () => void;
  tooltip?: ReactNode;
}) {
  const checkboxId = useId();
  const descriptionId = useId();

  const toggle = (
    <Label
      className={cn(
        "flex h-9 shrink-0 cursor-pointer select-none items-center gap-2 rounded-lg border border-input px-3 font-normal transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-ring/60 hover:border-foreground/30 has-data-[checked]:border-foreground/45 has-data-[indeterminate]:border-foreground/45 has-data-[checked]:bg-muted/30 has-data-[indeterminate]:bg-muted/30 dark:bg-input/30",
        className
      )}
      htmlFor={checkboxId}
    >
      <Checkbox
        aria-describedby={tooltip ? descriptionId : undefined}
        checked={indeterminate ? false : checked}
        id={checkboxId}
        indeterminate={indeterminate}
        onCheckedChange={() => onCheckedChange()}
      />
      <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-sm">
        <span className="font-medium">{label}</span>
      </span>
    </Label>
  );

  if (!tooltip) {
    return toggle;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={toggle} />
        <TooltipContent className="max-w-xs text-left" side="bottom">
          {tooltip}
        </TooltipContent>
      </Tooltip>
      {/*
       * The visual tooltip's aria-describedby binds to its trigger (the label),
       * not the checkbox. Mirror the text in a visually-hidden sibling — outside
       * the label so it never leaks into the control's accessible name — and
       * point the checkbox's aria-describedby at it, so the explanation reaches
       * assistive tech on the control itself.
       */}
      <span className="sr-only" id={descriptionId}>
        {tooltip}
      </span>
    </TooltipProvider>
  );
}
